import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Tooltip,
  Button,
  Rating,
} from "@mui/material";
import { Loader } from "components/Loader";
import {
  AddCircleOutline,
  RemoveCircleOutline,
  Edit as EditIcon,
  Send as SubmitIcon,
  DoubleArrow,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { themes } from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import { UserDetailsRow } from "components/CustomComponents/UserDetailsRow";
import { CustomButton } from "components/CustomComponents";
import { CustomButtonWithOutLoader } from "components/CustomComponents/CustomButton";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";

// ... [TimeInput Component and timeToMinutes helper remain unchanged] ...
const TimeInput = ({
  value,
  onChange,
  placeholder,
  sx,
  readOnly,
  disabled,
  ...props
}) => {
  const handleChange = (e) => {
    if (readOnly || disabled) return;
    let input = e.target.value.replace(/[^0-9]/g, "");
    if (input.length > 2) {
      input = input.substring(0, 2) + ":" + input.substring(2, 4);
    }
    if (input.length > 5) return;
    onChange(input);
  };

  const handleBlur = () => {
    if (!value || readOnly || disabled) return;
    if (value.length < 5 || !value.includes(":")) {
      showToast("warning", "Invalid time format. Use HH:MM");
      return;
    }
    const [hh, mm] = value.split(":").map(Number);
    if (hh > 17 || (hh === 17 && mm > 0)) {
      showToast("error", "Max allowed time is 17:00");
    }
    if (mm > 59) showToast("error", "Invalid minutes");
  };

  return (
    <TextField
      {...props}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder || "HH:MM"}
      sx={sx}
      disabled={disabled}
      inputProps={{ maxLength: 5, readOnly: readOnly }}
    />
  );
};

const timeToMinutes = (timeStr) => {
  if (!timeStr || !timeStr.includes(":")) return 0;
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
};

const EODViewPanel = ({
  onClose,
  reportId,
  reCallApi,
  hasProjectAccess,
  viewMode = "my_zone", // 'my_zone' or 'team_zone'
}) => {
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const isTeamMode = viewMode === "team_zone";

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // General Edit Mode
  const [submitting, setSubmitting] = useState(false);
  const [reportData, setReportData] = useState(null);

  // --- NEW STATES FOR PLAN EDIT ---
  const [isPlanEditMode, setIsPlanEditMode] = useState(false);
  //const [planEditReason, setPlanEditReason] = useState("");
  const [originalPlanData, setOriginalPlanData] = useState([]); // Backup for cancel
  const [planSaveLoading, setPlanSaveLoading] = useState(false);

  const getIsPastDate = (date) => {
    if (!date) return false;

    const parsed = dayjs(date, "DD-MM-YYYY", true);
    if (!parsed.isValid()) return false;

    return parsed.isBefore(dayjs(), "day");
  };

  const isPastDate = getIsPastDate(reportData?.report_date);
  console.log(
    "Report Date:",
    reportData?.report_date,
    "Is Past Date:",
    isPastDate,
  );

  const [managerInputs, setManagerInputs] = useState({
    rating: 0,
    comments: "",
    reopenReason: "",
  });
  const [showReopenField, setShowReopenField] = useState(false);

  const isSubmitted =
    (reportData &&
      (reportData.status === "Submitted" ||
        reportData.is_submitted ||
        reportData.status === "Approved")) ||
    false;

  // Form Data State
  const [formData, setFormData] = useState({
    report_date: "",
    total_worked_hours: "",
    planned_tasks_today: [],
    achieved_tasks: [],
    next_session_tasks: [],
    blockers_notes: "",
  });

  const [projectTaskList, setProjectTaskList] = useState([]);

  // --- Fetch Report Details ---
  const fetchReportDetails = useCallback(async () => {
    if (!domain || !reportId) return;
    setLoading(true);
    try {
      const res = await customGetAPI(`${domain}eod-reports/${reportId}/`);
      if (res.ok) {
        const data = res.responseDetails || res.data;
        setReportData(data);

        setManagerInputs((prev) => ({
          ...prev,
          rating: parseInt(data.ratings) || 0,
          comments: data.approver_comments || "",
        }));

        setFormData({
          report_date: data.report_date,
          total_worked_hours: data.total_worked_hours || "00:00",
          blockers_notes: data.blockers_notes || "",
          planned_tasks_today: (data.tasks_planned_for_today_actual || []).map(
            (t) => ({
              // Ensure we map all necessary fields for editing
              task_id: t.task_id,
              task_no: t.task_no || "-",
              task_name: t.task_name || "",
              planned_hours: t.planned_hours
                ? t.planned_hours.substring(0, 5)
                : "",
              remaining_eta: t.remaining_eta || "",
            }),
          ),
          achieved_tasks: (data.achieved_tasks || []).map((t) => ({
            id: t.id,
            task_id: t.task_id,
            task_no: t.task_no || "-",
            task_name: t.task_name || "",
            worked_hours: t.worked_hours || "00:00",
            is_manual: t.is_manual_entry,
          })),
          next_session_tasks: (data.tasks_planning_for_tomorrow || []).map(
            (t) => ({
              id: t.id,
              task_id: t.task_id,
              task_no: t.task_no || "-",
              task_name: t.task_name || "",
              remaining_eta: t.remaining_eta || "00:00",
              planned_hours: t.planned_hours
                ? t.planned_hours.substring(0, 5)
                : "00:00",
              sequence_order: t.sequence_order,
            }),
          ),
        });
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      showToast("error", "Failed to load report details");
    } finally {
      setLoading(false);
    }
  }, [domain, reportId]);

  // --- Fetch Available Tasks ---
  const fetchAvailableTasks = useCallback(async () => {
    if (!hasProjectAccess || !userDetails?.id) return;
    try {
      const res = await customGetAPI(
        `${domain}eod-reports/available_tasks/?user_id=${userDetails.id}`,
      );
      if (res.ok) {
        const tasks = Array.isArray(res.responseDetails)
          ? res.responseDetails
          : res.data || [];
        setProjectTaskList(
          tasks.map((t) => ({
            task_id: t.id,
            task_no: t.task_code,
            task_name: t.taskname,
            remaining_eta: t.remaining_eta || "00:00",
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching tasks", error);
    }
  }, [domain, userDetails.id, hasProjectAccess]);

  useEffect(() => {
    fetchReportDetails();
  }, [fetchReportDetails]);

  // Fetch tasks if General Edit OR Plan Edit is active (Project Mode)
  useEffect(() => {
    if ((isEditMode || isPlanEditMode) && hasProjectAccess && !isTeamMode) {
      fetchAvailableTasks();
    }
  }, [
    isEditMode,
    isPlanEditMode,
    hasProjectAccess,
    isTeamMode,
    fetchAvailableTasks,
  ]);

  // --- PLAN EDIT SPECIFIC HANDLERS ---

  const handlePlanEditToggle = () => {
    if (!isPlanEditMode) {
      // Entering Mode: Backup data
      setOriginalPlanData([...formData.planned_tasks_today]);
      //setPlanEditReason("");
      setIsPlanEditMode(true);
    } else {
      // Canceling Mode: Restore data
      setFormData((prev) => ({
        ...prev,
        planned_tasks_today: originalPlanData,
      }));
      setIsPlanEditMode(false);
      //setPlanEditReason("");
    }
  };

  const updatePlanRow = (index, field, value) => {
    const newList = [...formData.planned_tasks_today];
    // Add time validation logic here if needed similar to other rows
    newList[index] = { ...newList[index], [field]: value };
    setFormData((prev) => ({ ...prev, planned_tasks_today: newList }));
  };

  const addPlanRow = () => {
    const newItem = {
      task_id: null,
      task_no: "",
      task_name: "",
      planned_hours: "",
      remaining_eta: "",
    };
    setFormData((prev) => ({
      ...prev,
      planned_tasks_today: [...prev.planned_tasks_today, newItem],
    }));
  };

  const removePlanRow = (index) => {
    const newList = [...formData.planned_tasks_today];
    newList.splice(index, 1);
    setFormData((prev) => ({ ...prev, planned_tasks_today: newList }));
  };

  const handleSaveCurrentPlan = async () => {
    // if (!planEditReason.trim()) {
    //   showToast("error", "Reason is required to update the plan.");
    //   return;
    // }
    // Validation
    const tasks = formData.planned_tasks_today;
    if (tasks.length === 0) {
      showToast("error", "At least one task must be planned.");
      return;
    }
    if (hasProjectAccess) {
      if (
        tasks.some(
          (t) => !t.task_id || !t.planned_hours || t.planned_hours.length < 5,
        )
      ) {
        showToast(
          "error",
          "Please select tasks and enter valid planned hours.",
        );
        return;
      }
    } else {
      if (tasks.some((t) => !t.task_name.trim())) {
        showToast("error", "Please enter task names.");
        return;
      }
    }

    setPlanSaveLoading(true);

    const payload = {
      has_project_access: hasProjectAccess,
      //reason: planEditReason,
      current_day_plan: tasks.map((t, index) => {
        const item = {
          task_name: t.task_name,
          sequence_order: index + 1,
        };
        if (hasProjectAccess) {
          item.task_id = t.task_id;
          item.planned_hours = t.planned_hours;
        }
        return item;
      }),
    };

    try {
      const res = await customAPI(
        `${domain}eod-reports/${reportId}/update_current_day_plan/`,
        "POST",
        payload,
      );
      if (res.ok) {
        showToast("success", "Planned tasks updated successfully");
        setIsPlanEditMode(false);
        reCallApi(true);
        fetchReportDetails();
      } else {
        showToast("error", res.message || "Failed to update plan");
      }
    } catch (e) {
      showToast("error", "Error updating plan");
    } finally {
      setPlanSaveLoading(false);
    }
  };

  // --- GENERAL EDIT HANDLERS ---
  const handleEditToggle = () => {
    setIsEditMode(!isEditMode);
    if (isEditMode) fetchReportDetails();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Existing updateRow, removeRow, addRow for General Edit Mode...
  const updateRow = (listName, index, field, value) => {
    const newList = [...formData[listName]];
    if (listName === "next_session_tasks" && field === "planned_hours") {
      const task = newList[index];
      if (hasProjectAccess && task.remaining_eta && value.length === 5) {
        if (timeToMinutes(value) > timeToMinutes(task.remaining_eta)) {
          showToast(
            "error",
            `Cannot exceed Remaining ETA (${task.remaining_eta})`,
          );
          return;
        }
      }
    }
    newList[index][field] = value;
    setFormData((prev) => ({ ...prev, [listName]: newList }));
  };

  const removeRow = (listName, index) => {
    const newList = [...formData[listName]];
    newList.splice(index, 1);
    setFormData((prev) => ({ ...prev, [listName]: newList }));
  };

  const addRow = (listName) => {
    const newItem =
      listName === "achieved_tasks"
        ? { task_name: "", worked_hours: "" }
        : {
            task_id: null,
            task_no: "",
            task_name: "",
            remaining_eta: "",
            planned_hours: "",
          };
    setFormData((prev) => ({
      ...prev,
      [listName]: [...prev[listName], newItem],
    }));
  };

  const canAddNextRow = (list, type) => {
    if (list.length === 0) return true;
    const lastItem = list[list.length - 1];
    if (!lastItem.task_name || !lastItem.task_name.trim()) return false;
    if (type === "project_planned") {
      if (!lastItem.task_id) return false;
      if (!lastItem.planned_hours || lastItem.planned_hours.length < 5)
        return false;
    }
    return true;
  };

  const isSaveDisabled = () => {
    if (timeToMinutes(formData.total_worked_hours) > timeToMinutes("17:00")) {
      return true;
    }
    if (!hasProjectAccess) {
      if (
        !formData.total_worked_hours ||
        formData.total_worked_hours === "Invalid Date"
      )
        return true;
      if (formData.achieved_tasks.length === 0) return true;
      if (formData.achieved_tasks.some((t) => !t.task_name.trim())) return true;
    }
    if (formData.next_session_tasks.length === 0) return true;
    if (hasProjectAccess) {
      if (
        formData.next_session_tasks.some(
          (t) => !t.task_id || !t.planned_hours || t.planned_hours.length < 5,
        )
      )
        return true;

      const hasExceededTime = formData.next_session_tasks.some(
        (t) =>
          t.remaining_eta &&
          timeToMinutes(t.planned_hours) > timeToMinutes(t.remaining_eta),
      );
      if (hasExceededTime) return true;
    } else {
      if (formData.next_session_tasks.some((t) => !t.task_name.trim()))
        return true;
    }
    return false;
  };

  const handleUpdate = async () => {
    if (isSaveDisabled()) {
      showToast("warning", "Please fill all required fields correctly.");
      return;
    }
    setLoading(true);
    // ... [Original handleUpdate logic] ...
    let payload = {
      user_id: userDetails.id,
      report_date: formData.report_date,
      has_project_access: hasProjectAccess,
      blockers_notes: formData.blockers_notes,
    };

    if (hasProjectAccess) {
      payload.planned_tasks = formData.next_session_tasks.map((t, index) => ({
        task_id: t.task_id,
        planned_hours:
          t.planned_hours.length === 5
            ? `${t.planned_hours}:00`
            : t.planned_hours,
        sequence_order: index + 1,
      }));
    } else {
      payload.total_worked_hours = formData.total_worked_hours;
      payload.achieved_tasks = formData.achieved_tasks.map((t) => ({
        task_name: t.task_name,
      }));
      payload.planned_tasks = formData.next_session_tasks.map((t, index) => ({
        task_name: t.task_name,
        sequence_order: index + 1,
      }));
    }

    try {
      const res = await customAPI(
        `${domain}eod-reports/${reportId}/`,
        "PUT",
        payload,
      );
      if (res.ok) {
        showToast("success", "Report Updated Successfully");
        setIsEditMode(false);
        reCallApi(true);
        fetchReportDetails();
      } else {
        showToast("error", res.message || "Update Failed");
      }
    } catch (err) {
      showToast("error", "Error updating report");
    }
  };

  const handleSubmitReport = async () => {
    // ... [Original handleSubmit logic] ...
    if (!reportId) return;
    setSubmitting(true);
    try {
      const res = await customAPI(
        `${domain}eod-reports/${reportId}/submit/`,
        "POST",
      );
      if (res.ok) {
        showToast("success", "Report submitted successfully");
        reCallApi(true);
        fetchReportDetails();
      } else {
        showToast("error", res.message || "Submit Failed");
      }
    } catch (err) {
      showToast("error", "Error submitting report");
    } finally {
      setSubmitting(false);
    }
  };

  // ... [Manager Actions: handleApprove, handleReopen remain unchanged] ...
  const handleApprove = async () => {
    if (!managerInputs.rating || !managerInputs.comments) {
      showToast("error", "Rating and Comments are required to approve.");
      return;
    }
    setActionLoading(true);
    try {
      const payload = {
        approver_comments: managerInputs.comments,
        ratings: managerInputs.rating,
      };
      const res = await customAPI(
        `${domain}eod-reports/${reportId}/approve/`,
        "PUT",
        payload,
      );
      if (res.ok) {
        showToast("success", "Report Approved Successfully");
        reCallApi(true);
        onClose();
      } else {
        showToast("error", res.message || "Approval Failed");
      }
    } catch (e) {
      showToast("error", "Error approving report");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopen = async () => {
    if (!managerInputs.reopenReason) {
      showToast("error", "Please provide a reason for reopening.");
      return;
    }
    setActionLoading(true);
    try {
      const payload = {
        reason: managerInputs.reopenReason,
        reopened_by_user_id: userDetails.id,
      };
      const res = await customAPI(
        `${domain}eod-reports/${reportId}/reopen/`,
        "POST",
        payload,
      );
      if (res.ok) {
        showToast("success", "Report Reopened Successfully");
        reCallApi(true);
        onClose();
      } else {
        showToast("error", res.message || "Reopen Failed");
      }
    } catch (e) {
      showToast("error", "Error reopening report");
    } finally {
      setActionLoading(false);
    }
  };

  const styles = {
    manualBox: (type) => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      p: 0.5,
      mb: 1.5,
      borderRadius: "8px",
      backgroundColor: type === "planned" ? "#FFF9E6" : "#F1FDF9",
      border: `1px solid ${type === "planned" ? "#FFD700" : "#00D4AA"}`,
    }),
    boxInput: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "transparent",
        "& fieldset": { border: "none" },
      },
      "& .MuiOutlinedInput-input": { padding: "10px", fontSize: "0.95rem" },
    },
    tableHead: (bgcolor) => ({
      backgroundColor: bgcolor,
      "& th": {
        fontWeight: "bold",
        fontSize: "0.8rem",
        color: "#333",
        borderBottom: "1px solid #ddd",
        whiteSpace: "nowrap",
        py: 1,
        px: 1,
      },
    }),
    selectInput: {
      "& .MuiOutlinedInput-root": { padding: "2px 5px !important" },
      "& .MuiOutlinedInput-input": { fontSize: "0.85rem" },
    },
  };

  if (loading && !reportData) return <Loader />;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: "#fff",
        }}
      >
        {/* HEADER */}
        <Paper
          elevation={0}
          sx={{
            borderBottom: `1px solid ${themes?.miliedLightGray}`,
            borderRadius: 0,
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
                py: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  onClick={() => {
                    if (isEditMode) {
                      setIsEditMode(false);
                      setIsPlanEditMode(false);
                      fetchReportDetails();
                    } else if (isPlanEditMode) {
                      handlePlanEditToggle(); // Cancel plan edit
                    } else onClose();
                  }}
                  sx={{
                    color: themes?.grayColor,
                    "&:hover": { backgroundColor: themes?.whiteColor },
                  }}
                >
                  <DoubleArrow />
                </IconButton>
                <Box sx={{ ml: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {isTeamMode
                      ? "Review EOD Report"
                      : isEditMode
                        ? "Edit EOD Report"
                        : isPlanEditMode
                          ? "Edit Planned Tasks"
                          : "View EOD Report"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isTeamMode
                      ? "Approve or Reopen team member's report."
                      : isEditMode
                        ? "Update your daily work progress."
                        : isPlanEditMode
                          ? "Update current day's plan."
                          : "Details of submitted report."}
                  </Typography>
                </Box>
              </Box>

              {/* My Zone Actions - HIDE IF IN PLAN EDIT MODE */}
              {!isTeamMode && !isEditMode && !isPlanEditMode && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip
                    title={isSubmitted ? "Already submitted" : "Submit"}
                    arrow
                    slotProps={tooltipStyleProps}
                  >
                    <span>
                      <IconButton
                        onClick={handleSubmitReport}
                        disabled={submitting || isSubmitted}
                        sx={{
                          color: themes?.greenColor,
                          backgroundColor: themes?.primary,
                          borderRadius: "50%",
                          "&:hover": { backgroundColor: themes?.primary },
                        }}
                      >
                        <SubmitIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={isSubmitted ? "Submitted - cannot edit" : "Edit"}
                    arrow
                    slotProps={tooltipStyleProps}
                  >
                    <span>
                      <IconButton
                        onClick={handleEditToggle}
                        disabled={isSubmitted}
                        sx={{
                          color: themes?.greenColor,
                          backgroundColor: themes?.primary,
                          borderRadius: "50%",
                          "&:hover": { backgroundColor: themes?.primary },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>

        <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <UserDetailsRow
              userDetails={{
                image_url: reportData?.image_url,
                employee_id: reportData?.employee_id,
                user_name: reportData?.user_name,
                department_name: reportData?.department,
                designation: reportData?.designation,
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="body2">
              <strong>Date:</strong> {formData.report_date}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">
                <strong>Worked Hours:</strong>
              </Typography>
              <TimePicker
                value={
                  formData.total_worked_hours
                    ? dayjs(formData.total_worked_hours, "HH:mm")
                    : null
                }
                onChange={(newValue) => {
                  const timeStr = newValue ? newValue.format("HH:mm") : "";
                  // 17:00 Validation
                  const [hh, mm] = timeStr.split(":").map(Number);
                  if (hh > 17 || (hh === 17 && mm > 0)) {
                    showToast("error", "Max allowed time is 17:00");
                  }
                  handleChange("total_worked_hours", timeStr);
                }}
                ampm={false}
                readOnly={!isEditMode || hasProjectAccess}
                slotProps={{
                  textField: {
                    size: "small",
                    placeholder: "HH:MM",
                    sx: {
                      width: 120,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        height: 40,
                        bgcolor:
                          !isEditMode || hasProjectAccess ? "#f5f5f5" : "#fff",
                      },
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* 1. PLANNED TASKS - CONDITIONALLY EDITABLE */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Planned Tasks
              </Typography>
              {/* PLAN EDIT TOGGLE BUTTON */}
              {!isTeamMode &&
                !isSubmitted &&
                isEditMode &&
                !isPlanEditMode &&
                !isPastDate && (
                  <Tooltip
                    title="Edit Plan"
                    arrow
                    slotProps={tooltipStyleProps}
                  >
                    <IconButton
                      size="small"
                      onClick={handlePlanEditToggle}
                      sx={{ color: themes.primaryButton }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              The tasks planned for this day.
            </Typography>

            {hasProjectAccess ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <Table size="small">
                  <TableHead sx={styles.tableHead("#FFF9E6")}>
                    <TableRow>
                      <TableCell>Task No</TableCell>
                      <TableCell width={isPlanEditMode ? "50%" : "auto"}>
                        Task Name
                      </TableCell>
                      <TableCell>Planned hrs</TableCell>
                      <TableCell>Remaining ETA</TableCell>
                      {isPlanEditMode && <TableCell width="5%"></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.planned_tasks_today.length > 0 ? (
                      formData.planned_tasks_today.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.task_no}</TableCell>
                          <TableCell>
                            {/* PROJECT MODE EDITABLE NAME */}
                            {isPlanEditMode ? (
                              <Autocomplete
                                size="small"
                                options={projectTaskList}
                                getOptionLabel={(option) =>
                                  option.task_name || ""
                                }
                                value={
                                  projectTaskList.find(
                                    (t) => t.task_id === row.task_id,
                                  ) || null
                                }
                                onChange={(e, val) => {
                                  if (val) {
                                    const updatedList = [
                                      ...formData.planned_tasks_today,
                                    ];
                                    updatedList[index] = {
                                      ...updatedList[index],
                                      task_id: val.task_id,
                                      task_no: val.task_no,
                                      task_name: val.task_name,
                                      remaining_eta: val.remaining_eta,
                                    };
                                    setFormData({
                                      ...formData,
                                      planned_tasks_today: updatedList,
                                    });
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Select"
                                    sx={styles.selectInput}
                                  />
                                )}
                              />
                            ) : (
                              row.task_name
                            )}
                          </TableCell>
                          <TableCell>
                            {/* PROJECT MODE EDITABLE TIME */}
                            {isPlanEditMode ? (
                              <TimeInput
                                value={row.planned_hours}
                                onChange={(val) =>
                                  updatePlanRow(index, "planned_hours", val)
                                }
                                sx={{
                                  width: "80px",
                                  "& .MuiOutlinedInput-input": { p: "8px" },
                                  bgcolor: !row.task_id
                                    ? "#f5f5f5"
                                    : "transparent",
                                }}
                                disabled={!row.task_id}
                              />
                            ) : row.planned_hours ? (
                              row.planned_hours.substring(0, 5)
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>{row.remaining_eta || "-"}</TableCell>
                          {isPlanEditMode && (
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removePlanRow(index)}
                              >
                                <RemoveCircleOutline fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={isPlanEditMode ? 5 : 4}
                          align="center"
                          sx={{
                            py: 3,
                            fontStyle: "italic",
                            color: "text.secondary",
                          }}
                        >
                          No planned tasks found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              // MANUAL MODE
              <Box>
                {formData.planned_tasks_today.length > 0 ? (
                  formData.planned_tasks_today.map((task, i) => (
                    <Box key={i} sx={styles.manualBox("planned")}>
                      {isPlanEditMode ? (
                        <TextField
                          fullWidth
                          value={task.task_name}
                          onChange={(e) =>
                            updatePlanRow(i, "task_name", e.target.value)
                          }
                          sx={styles.boxInput}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ px: 2, py: 1, fontWeight: 500 }}
                        >
                          {task.task_name}
                        </Typography>
                      )}
                      {isPlanEditMode && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removePlanRow(i)}
                        >
                          <RemoveCircleOutline />
                        </IconButton>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    No planned tasks found
                  </Typography>
                )}
              </Box>
            )}

            {/* ADD ROW BUTTON FOR PLAN EDIT */}
            {isPlanEditMode && (
              <Box sx={{ mt: 1 }}>
                <IconButton onClick={addPlanRow} sx={{ color: "#FFD700" }}>
                  <AddCircleOutline />
                </IconButton>
              </Box>
            )}

            {/* REASON FIELD FOR PLAN EDIT */}
            {/* {isPlanEditMode && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="error" fontWeight="bold">
                  Reason for change *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Enter reason for changing the plan..."
                  value={planEditReason}
                  onChange={(e) => setPlanEditReason(e.target.value)}
                  sx={{ mt: 0.5, bgcolor: "#fff" }}
                />
              </Box>
            )} */}
          </Box>

          {/* 2. ACHIEVED TASKS */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Achieved Tasks
            </Typography>
            {hasProjectAccess ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <Table size="small">
                  <TableHead sx={styles.tableHead("#F1FDF9")}>
                    <TableRow>
                      <TableCell width="15%">Task No</TableCell>
                      <TableCell width="65%">Task Name</TableCell>
                      <TableCell width="20%">Worked Hrs</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.achieved_tasks.length > 0 ? (
                      formData.achieved_tasks.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.task_no}</TableCell>
                          <TableCell>{row.task_name}</TableCell>
                          <TableCell>
                            {row.worked_hours.substring(0, 5)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{
                            py: 3,
                            fontStyle: "italic",
                            color: "text.secondary",
                          }}
                        >
                          No achieved tasks found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box>
                {formData.achieved_tasks.map((row, index) => (
                  <Box key={index} sx={styles.manualBox("achieved")}>
                    {isEditMode ? (
                      <TextField
                        fullWidth
                        value={row.task_name}
                        onChange={(e) =>
                          updateRow(
                            "achieved_tasks",
                            index,
                            "task_name",
                            e.target.value,
                          )
                        }
                        sx={styles.boxInput}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                        {row.task_name}
                      </Typography>
                    )}
                    {isEditMode && index > 0 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeRow("achieved_tasks", index)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {isEditMode &&
                  canAddNextRow(formData.achieved_tasks, "manual_achieved") && (
                    <IconButton
                      onClick={() => addRow("achieved_tasks")}
                      sx={{ color: "#00D4AA" }}
                    >
                      <AddCircleOutline />
                    </IconButton>
                  )}
                {!isEditMode && formData.achieved_tasks.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    No achieved tasks found
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* 3. PLANNED FOR NEXT SESSION */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Planned for Next Session
            </Typography>
            {hasProjectAccess ? (
              <Box>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  <Table size="small">
                    <TableHead sx={styles.tableHead("#FFF9E6")}>
                      <TableRow>
                        <TableCell width="15%">Task No</TableCell>
                        <TableCell width="50%">Task Name</TableCell>
                        <TableCell width="15%">Remaining ETA</TableCell>
                        <TableCell width="15%">Planned</TableCell>
                        {isEditMode && <TableCell width="5%"></TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.next_session_tasks.length > 0 ? (
                        formData.next_session_tasks.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>{row.task_no || "-"}</TableCell>
                            <TableCell>
                              {isEditMode ? (
                                <Autocomplete
                                  size="small"
                                  options={projectTaskList}
                                  getOptionLabel={(option) =>
                                    option.task_name || ""
                                  }
                                  value={
                                    projectTaskList.find(
                                      (t) => t.task_id === row.task_id,
                                    ) || null
                                  }
                                  onChange={(e, val) => {
                                    if (val) {
                                      const updatedList = [
                                        ...formData.next_session_tasks,
                                      ];
                                      updatedList[index] = {
                                        ...updatedList[index],
                                        ...val,
                                      };
                                      setFormData({
                                        ...formData,
                                        next_session_tasks: updatedList,
                                      });
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select"
                                      sx={styles.selectInput}
                                    />
                                  )}
                                />
                              ) : (
                                <Typography variant="body2">
                                  {row.task_name}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{row.remaining_eta || "-"}</TableCell>
                            <TableCell>
                              <TimeInput
                                value={row.planned_hours}
                                onChange={(val) =>
                                  updateRow(
                                    "next_session_tasks",
                                    index,
                                    "planned_hours",
                                    val,
                                  )
                                }
                                readOnly={!isEditMode || !row.task_id}
                                sx={{
                                  width: "80px",
                                  "& .MuiOutlinedInput-input": { p: "8px" },
                                  bgcolor:
                                    !isEditMode || !row.task_id
                                      ? "#f5f5f5"
                                      : "transparent",
                                }}
                              />
                            </TableCell>
                            {isEditMode && (
                              <TableCell>
                                {index > 0 && (
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      removeRow("next_session_tasks", index)
                                    }
                                  >
                                    <RemoveCircleOutline fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={isEditMode ? 5 : 4}
                            align="center"
                            sx={{
                              py: 3,
                              fontStyle: "italic",
                              color: "text.secondary",
                            }}
                          >
                            No tasks planned for next session
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {isEditMode &&
                  canAddNextRow(
                    formData.next_session_tasks,
                    "project_planned",
                  ) && (
                    <IconButton
                      onClick={() => addRow("next_session_tasks")}
                      sx={{ mt: 1, color: "#FFD700" }}
                    >
                      <AddCircleOutline />
                    </IconButton>
                  )}
              </Box>
            ) : (
              <Box>
                {formData.next_session_tasks.map((row, index) => (
                  <Box key={index} sx={styles.manualBox("planned")}>
                    {isEditMode ? (
                      <TextField
                        fullWidth
                        value={row.task_name}
                        onChange={(e) =>
                          updateRow(
                            "next_session_tasks",
                            index,
                            "task_name",
                            e.target.value,
                          )
                        }
                        sx={styles.boxInput}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                        {row.task_name}
                      </Typography>
                    )}
                    {isEditMode && index > 0 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeRow("next_session_tasks", index)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {isEditMode &&
                  canAddNextRow(
                    formData.next_session_tasks,
                    "manual_planned",
                  ) && (
                    <IconButton
                      onClick={() => addRow("next_session_tasks")}
                      sx={{ color: "#FFD700" }}
                    >
                      <AddCircleOutline />
                    </IconButton>
                  )}
                {!isEditMode && formData.next_session_tasks.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    No tasks planned for next session
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* BLOCKERS */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Blockers / Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder={isEditMode ? "Add blockers..." : "No notes"}
              value={formData.blockers_notes}
              onChange={(e) => handleChange("blockers_notes", e.target.value)}
              InputProps={{ readOnly: !isEditMode }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: isEditMode ? "#fff" : "#f9f9f9",
                },
              }}
            />
          </Box>

          {/* MANAGER ACTION SECTION */}
          {isTeamMode && (
            // ... [Manager UI Code - unchanged]
            <Paper
              variant="outlined"
              sx={{ p: 2, mt: 4, bgcolor: "#f9f9f9", borderColor: "#ddd" }}
            >
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Manager Actions
              </Typography>
              {/* ... [Rest of manager UI] ... */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" display="block">
                    Rating (1-5)
                  </Typography>
                  <Rating
                    value={managerInputs.rating}
                    onChange={(e, val) =>
                      setManagerInputs({ ...managerInputs, rating: val })
                    }
                    disabled={!isSubmitted || showReopenField}
                  />
                </Box>
                <TextField
                  label="Approver Comments"
                  fullWidth
                  size="small"
                  value={managerInputs.comments}
                  onChange={(e) =>
                    setManagerInputs({
                      ...managerInputs,
                      comments: e.target.value,
                    })
                  }
                  disabled={!isSubmitted || showReopenField}
                  sx={{ bgcolor: "#fff" }}
                />
              </Box>

              {showReopenField && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="error" fontWeight="bold">
                    Reason for Reopening:
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Explain why this report is being reopened..."
                    value={managerInputs.reopenReason}
                    onChange={(e) =>
                      setManagerInputs({
                        ...managerInputs,
                        reopenReason: e.target.value,
                      })
                    }
                    sx={{ mt: 1, bgcolor: "#fff" }}
                  />
                </Box>
              )}
            </Paper>
          )}
        </Box>

        {/* FOOTER ACTIONS */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #eee",
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          {/* PLAN EDIT FOOTER - REPLACES OTHERS */}
          {isPlanEditMode && (
            <>
              <CustomButtonWithOutLoader
                actionFuntion={handlePlanEditToggle}
                actionName="Cancel"
                typeName="button"
                customTitleSx={{
                  color: "#666",
                  background: "transparent",
                  boxShadow: "none",
                }}
              />
              <CustomButton
                actionFuntion={handleSaveCurrentPlan}
                actionName="Save Plan"
                typeName="button"
                loading={planSaveLoading}
                // Disable if no reason or loading
                disableAction={
                  //!planEditReason.trim() ||
                  formData.planned_tasks_today.length === 0 || planSaveLoading
                }
                customTitleSx={{
                  bgcolor: "#00C853",
                  "&:hover": { bgcolor: "#00E676" },
                  px: 3,
                }}
              />
            </>
          )}

          {/* My Zone Footer (General Edit) */}
          {!isTeamMode && isEditMode && !isPlanEditMode && (
            <>
              <CustomButtonWithOutLoader
                actionFuntion={handleEditToggle}
                actionName="Cancel"
                typeName="button"
                customTitleSx={{
                  color: "#666",
                  background: "transparent",
                  boxShadow: "none",
                }}
              />
              <CustomButton
                actionFuntion={handleUpdate}
                actionName="Update"
                typeName="button"
                loading={loading} // Fixed prop name to match your component
                disableAction={isSaveDisabled() || loading}
                customTitleSx={{
                  bgcolor: "#00C853",
                  "&:hover": { bgcolor: "#00E676" },
                  px: 3,
                }}
              />
            </>
          )}

          {/* Team Zone Footer */}
          {isTeamMode && (
            <>
              {!showReopenField ? (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setShowReopenField(true)}
                    disabled={actionLoading}
                  >
                    Reopen
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleApprove}
                    disabled={!isSubmitted || actionLoading}
                    sx={{ color: "#fff", fontWeight: "bold" }}
                  >
                    Approve
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => setShowReopenField(false)}
                    disabled={actionLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleReopen}
                    disabled={actionLoading}
                  >
                    Confirm Reopen
                  </Button>
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default EODViewPanel;
